// 메인 페이지 Our story 타임라인의 '자세히 보기' 모달.
// 사건성 마일스톤(`caseSlug` 보유) 에는 "사례 보러 가기" CTA 가 노출되어
// 블로그 CASES 상세 페이지(/blog/post/:slug) 로 연결.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './MilestoneModal.module.css';

export function MilestoneModal({ milestone, onClose }) {
  // ESC 닫기 + body 스크롤 잠금
  useEffect(() => {
    if (!milestone) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [milestone, onClose]);

  if (!milestone) return null;

  const hasLinks = milestone.outboundLinks && milestone.outboundLinks.length > 0;
  const hasGallery = milestone.gallery && milestone.gallery.length > 0;
  const hasItems = milestone.items && milestone.items.length > 0;
  const hasCase = Boolean(milestone.caseSlug);

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`milestone-modal-title-${milestone.id}`}
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.close} onClick={onClose} aria-label="닫기">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <header className={styles.header}>
          <span className={styles.year}>{milestone.year}</span>
          <h2 id={`milestone-modal-title-${milestone.id}`} className={styles.title}>
            {milestone.title}
          </h2>
          {milestone.summary && <p className={styles.summary}>{milestone.summary}</p>}
        </header>

        {milestone.primaryImg && (
          <figure className={styles.primaryFigure}>
            <img src={milestone.primaryImg} alt={milestone.title} className={styles.primaryImg} />
          </figure>
        )}

        {hasItems && (
          <ul className={styles.items}>
            {milestone.items.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        )}

        {milestone.body && <p className={styles.body}>{milestone.body}</p>}

        {hasGallery && (
          <div className={styles.gallery}>
            {milestone.gallery.map((img, i) => (
              <figure key={i} className={styles.galleryFigure}>
                <img src={img} alt={`${milestone.title} 자료 ${i + 1}`} />
              </figure>
            ))}
          </div>
        )}

        {(hasCase || hasLinks) && (
          <div className={styles.actions}>
            {hasCase && (
              <Link
                to={`/blog/post/${milestone.caseSlug}`}
                className={styles.caseCta}
                onClick={onClose}
              >
                사례 보러 가기
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M3 8h10M9 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            )}
            {hasLinks &&
              milestone.outboundLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.outboundCta}
                >
                  {link.label}
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M6 3h7v7M13 3L6 10M11 11v2H3V5h2"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              ))}
          </div>
        )}

        {/* placeholder 는 사건성 마일스톤(isCase 미정 또는 true) 에서만 노출.
            회사 행사·인사 같은 비-사건성(isCase: false) 에는 표시 안 함. */}
        {!hasCase && !hasLinks && milestone.isCase !== false && (
          <p className={styles.placeholder}>상세 사례는 준비 중입니다.</p>
        )}
      </div>
    </div>
  );
}
